export const meta = {
  title: 'Ten conversations that save your first week abroad',
  description:
    'The conversations everyone actually has in week one — transit, SIM cards, groceries, and the pharmacy — and how to prepare for them before you land.',
  date: '2026-06-10',
  tags: ['travel-tips', 'getting-started'],
};

Moving somewhere new compresses a year of awkward conversations into one week.
These are the ten situations that come up almost immediately — and how to walk
into each one already knowing how it tends to go.

## 1. The SIM card counter

You will be asked for ID, a local address, and whether you want prepaid or a
contract. The opener is easy; it's the follow-ups that catch people. Practice
the branch where they ask for a residence permit you don't have yet — there's
almost always a prepaid option that skips it.

## 2. Transit tickets

Zone systems, day passes, and validation machines differ everywhere. The
question that matters isn't "how much is a ticket" — it's "which ticket covers
where I'm going, and do I need to validate it before boarding?"

## 3. The pharmacy

Describing symptoms is the one conversation where vague language has real
costs. Learn to say where it hurts, how long it's been, and what you're
allergic to. Pharmacists are also the fastest route to "do I need a doctor for
this?"

## 4. Groceries and the checkout question

Every country has its own checkout micro-script: bags, loyalty cards, cash or
card, receipt or not. It's thirty seconds long and you'll run it every day —
worth getting smooth.

## 5. Asking for the bill

In half the world the bill never comes until you ask. Knowing the polite local
gesture-plus-phrase combination saves you twenty minutes per meal, every meal.

## 6. The landlord viewing

Utilities included or not, deposit terms, minimum stay, and what "furnished"
actually means locally. Asking these in the landlord's language noticeably
changes how seriously you're treated.

## 7. Opening hours and "are you open?"

Holiday schedules, siesta hours, last-order times. One phrase — "until when
are you open today?" — prevents most of the closed-door surprises.

## 8. The bank or post office queue

Take-a-number systems and "which window do I need" are the boss fight of
bureaucratic vocabulary. Prepare the sentence that states your errand in one
line; everything after that is them guiding you.

## 9. Ordering when the menu has no pictures

You don't need food vocabulary — you need three meta-questions: "what is this?",
"what do you recommend?", and the allergy sentence. Those unlock any menu.

## 10. "Sorry, I didn't catch that"

The most valuable phrase in any language is the one that keeps the
conversation alive when you're lost: *slower, please — I'm still learning.*
Locals switch from transaction mode to helping mode almost every time.

---

Every one of these is an interactive scenario in WhatImean — a branching
conversation with native-speaker audio, not a phrase list. Download a pack
before you fly and practice them offline on the plane.
